import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { Menu } from '@ps/web-martech-cms-types';
import { TrackingCategory, TrackingContext } from '@ps/web-shared-analytics';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useStickyTopHeaderClassName } from '@swf/cms';
import StarsNavigationItem from '@swf/components/StarsNavigation/StarsNavigationItem';
import SearchIcon from '@swf/core/assets/casino/search-icon-casino.svg';
import Arrow from '@swf/core/assets/icons/arrow.svg';
import useDebounce from '@swf/core/hooks/useDebounce';
import { select } from '@swf/core/store';
import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';

import useCustomHeader from '../../hooks/useCustomHeader';
import useScrollGradient from '../../hooks/useGradientScrollbar';
import shared from '../../styles/shared.scss';
import { MenuView } from '../../types/MenuView';
import CMSMenuGraphProvider from '../CMSMenu/CMSMenuGraphProvider';

import { TXTWCL_Search } from './CMSNavigation.messages.json';
import styles from './CMSNavigation.scss';
import CMSNavigationItem from './components/CMSNavigationItem/CMSNavigationItem';
import CMSNavigationSubmenu from './components/CMSNavigationSubmenu/CMSNavigationSubmenu';
import DesktopPlayMoneyToggle from './components/DesktopPlayMoneyToggle/DesktopPlayMoneyToggle';
import PlayMoneyToggle from './components/PlayMoneyToggle/PlayMoneyToggle';
import Search from './components/Search/Search';
import useHideIconsOnMobile from './hooks/useHideIconsOnMobile';

const MENU_OPEN_DELAY = 400;

interface Props {
  menu: Menu;
  maxDepth: number;
  isSeoMenu?: boolean;
  classes?: {
    root?: string;
    container?: string;
    nav?: string;
    navItem?: string;
  };
}

const CMSNavigation: React.FC<Props> = ({ menu, maxDepth, isSeoMenu = false, classes }) => {
  const [open, setOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number>();
  const [searchOpen, setSearchOpen] = useState(false);
  const seoMenuRef = useRef<HTMLUListElement | null>(null);
  const { applyGradientBefore, applyGradientAfter } = useScrollGradient(isSeoMenu, seoMenuRef);
  const { children: submenu, highlighted_item: highlightedItem } = menu;
  const data = useSelector(select.cms.getContent);
  const menuView = useSelector(select.cms.getMenuView);
  const { lightHeader, prospectHeader } = useCustomHeader();
  const withTag = submenu.some((child) => !!(data[child.uid] as Menu)?.tag_text);
  const playMoney = useFeature(FeatureFlag.PlayMoney);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isMobile = useSelector(select.device.isMobile);
  const stickyTopHeaderClassName = useStickyTopHeaderClassName('casino');
  const brandId = useSelector(select.cms.getBrandId);
  const debounceSetOpen = useDebounce(() => setOpen(true), MENU_OPEN_DELAY);

  const showMenuIcons = useHideIconsOnMobile();

  const openSubmenu = useCallback(
    (index: number) => {
      isMobile ? setOpen(true) : debounceSetOpen();

      setOpenIndex(index);
    },
    [isMobile, debounceSetOpen]
  );

  const closeSubmenu = useCallback(() => {
    if (!isMobile) {
      debounceSetOpen.cancel();
    }
    setOpen(false);
  }, [isMobile, debounceSetOpen]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (seoMenuRef.current) {
      seoMenuRef.current.scrollBy({ left: direction === 'left' ? -150 : 150, behavior: 'smooth' });
    }
  }, []);

  useEffect(
    () => () => {
      debounceSetOpen.cancel();
    },
    [isMobile, debounceSetOpen]
  );

  const handleToggle = useCallback(() => setSearchOpen(!searchOpen), [searchOpen]);

  return !lightHeader && !prospectHeader && submenu.length ? (
    <TrackingContext.Provider
      value={{
        category: TrackingCategory.Stars,
        widget: { widgetName: 'CMS Navigation Widget', widgetId: 'cms-navigation-widget' }
      }}
    >
      <div
        id="top-nav-container"
        className={classNames(styles.container, stickyTopHeaderClassName, classes?.root, {
          [styles.searchOpen]: searchOpen,
          [styles.desktop]: isDesktopEmbedded,
          [styles.mobile]: isMobile,
          [styles.sticky]: brandId === BrandTypes.STARS_CASINO,
          [styles.seoMenu]: isSeoMenu,
          [shared.scrolled]: !showMenuIcons
        })}
      >
        <div
          className={classNames(styles.leftArrowContainer, {
            [styles.hideLeftArrow]: !applyGradientBefore
          })}
          onClick={() => scroll('left')}
        >
          <div className={styles.leftArrow}>
            <Arrow />
          </div>
        </div>
        <div
          className={classNames(styles.backgroundWrapper, classes?.container, {
            [styles.withTag]: withTag,
            [styles.casino]: brandId === BrandTypes.STARS_CASINO,
            [styles.seoMenu]: isSeoMenu
          })}
        >
          <nav
            className={classNames(styles.nav, classes?.nav, {
              [styles.applyGradient]: applyGradientBefore || applyGradientAfter,
              [styles.horizontallyScrollable]: brandId === BrandTypes.STARS_CASINO
            })}
          >
            <CMSMenuGraphProvider menu={menu}>
              <ul
                ref={seoMenuRef}
                className={classNames(styles.navList, {
                  [styles.withTag]: withTag,
                  [styles.horizontallyScrollable]: brandId !== BrandTypes.STARS_CASINO
                })}
                onMouseLeave={() => closeSubmenu()}
              >
                {submenu.map((item, index) => (
                  <React.Fragment key={item.uid}>
                    <li
                      className={classNames(styles.topLevel, classes?.navItem, {
                        [styles.seoMenu]: isSeoMenu
                      })}
                      onTouchEnd={() => openSubmenu(index)}
                      onMouseOver={() => openSubmenu(index)}
                    >
                      <CMSNavigationItem
                        reference={item}
                        maxDepth={maxDepth}
                        isSeoMenu={isSeoMenu}
                      />
                    </li>
                    <li className={styles.subNav}>
                      {open && openIndex === index && maxDepth > 1 && (
                        <CMSNavigationSubmenu menuItemRef={item} onSelect={closeSubmenu} />
                      )}
                    </li>
                  </React.Fragment>
                ))}

                {!!highlightedItem?.item?.length && (
                  <>
                    <li
                      className={classNames(styles.topLevel, classes?.navItem, styles.highlighted, {
                        [styles.seoMenu]: isSeoMenu,
                        [styles.withBackgroundImage]: highlightedItem.background_image?.url
                      })}
                      style={getBackgroundImageStyle(highlightedItem.background_image?.url)}
                      onTouchEnd={() => openSubmenu(submenu.length)}
                      onMouseOver={() => openSubmenu(submenu.length)}
                    >
                      <CMSNavigationItem
                        reference={highlightedItem.item[0]}
                        maxDepth={maxDepth}
                        isSeoMenu={isSeoMenu}
                        activeItem={styles.active}
                        tagItem={styles.withTag}
                      />
                    </li>
                    <li className={classNames(styles.subNav, styles.highlightedSubmenu)}>
                      {open && openIndex === submenu.length && maxDepth > 1 && (
                        <CMSNavigationSubmenu
                          menuItemRef={highlightedItem.item[0]}
                          onSelect={closeSubmenu}
                        />
                      )}
                    </li>
                  </>
                )}
              </ul>
            </CMSMenuGraphProvider>
            {brandId === BrandTypes.STARS_CASINO && !isSeoMenu && (
              <div className={styles.right}>
                {(isDesktopEmbedded || isMobile) && (
                  <StarsNavigationItem
                    tagName="button"
                    className={classNames(styles.searchIcon, styles.item, {
                      [styles.mobile]: isMobile
                    })}
                    active={searchOpen}
                    onClick={handleToggle}
                  >
                    <SearchIcon />
                    <span className={shared.hideOnScroll}>
                      {menuView === MenuView.iconsAndText && <Translation {...TXTWCL_Search} />}
                    </span>
                  </StarsNavigationItem>
                )}
                {playMoney && (
                  <section className={styles.item}>
                    {isDesktopEmbedded ? <DesktopPlayMoneyToggle /> : <PlayMoneyToggle />}
                  </section>
                )}
                {((!isDesktopEmbedded && !isMobile) || searchOpen) && (
                  <Search className={classNames(styles.item)} onToggle={handleToggle} />
                )}
              </div>
            )}
          </nav>
        </div>
        <div
          className={classNames(styles.rightArrowContainer, {
            [styles.hideRightArrow]: !applyGradientAfter
          })}
          onClick={() => scroll('right')}
        >
          <div className={styles.rightArrow}>
            <Arrow />
          </div>
        </div>
      </div>
    </TrackingContext.Provider>
  ) : null;
};

export default CMSNavigation;
